import { SimpleSearchFilter } from 'module/api/filter/types';
import { ConditionTypes } from 'module/api/integrations/salesforce/types';
import { defaultFilter } from 'module/types/filter-field/filter';
import { getApiFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-filter-transformer';
import { keyBy } from 'lodash';
import { useSalesForceMatchFieldsQuery } from 'queries/salesforce';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { BlockList } from 'components/organisms/signal/detail/block-list/list';
import { convertIntegrationFieldToField } from 'components/pages/integrations/salesforce/utils/field-conversion';
import { useAutomationContext } from './automation-context';
import { Content } from './common/content/content';
import { TriggerSelectionDrawer } from './trigger-selection-drawer';

export const AutomationMatchingConditons: FC = () => {
  const { integrationId, automationId } = useParams<{
    integrationId: string;
    automationId: string;
  }>();

  const isEdit = useMemo(() => Boolean(automationId), [automationId]);

  const { automationDetails, setAutomationDetails } = useAutomationContext();

  const { salesforceObject, matches } = automationDetails || {};

  const { value } = salesforceObject || {};

  const { data: fields, status, isFetching } = useSalesForceMatchFieldsQuery(
    parseInt(integrationId || '0'),
    String(value) || '',
    {
      enabled: !!value,
    },
  );

  useEffect(() => {
    // Enhanced guard: Check if matches exists AND has the required structure
    if (
      isEdit &&
      fields &&
      fields.length > 0 &&
      automationDetails?.matches &&
      typeof automationDetails.matches === 'object' &&
      'match' in automationDetails.matches &&
      Object.keys(automationDetails.matches).length > 1
    ) {
      const convertedFields = convertIntegrationFieldToField(fields);

      if (convertedFields && convertedFields.length > 0) {
        const lookup = keyBy(convertedFields, 'fieldId');
        const transform = getApiFilterTransformer(lookup);

        setAutomationDetails?.({
          matches: transform.from(
            automationDetails.matches as unknown as SimpleSearchFilter,
          ),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, isEdit]);

  const [triggerSelectionDrawerVisible, setTriggerSelectionDrawerVisible] =
    useState(false);
  const openTriggerSelectionDrawer = useCallback(() => {
    setTriggerSelectionDrawerVisible(true);
  }, []);
  const closeTriggerSelectionDrawer = useCallback(() => {
    setTriggerSelectionDrawerVisible(false);
  }, []);

  return (
    <>
      <BlockList.Item
        disabled={!value || status === 'pending' || isFetching}
        onClick={openTriggerSelectionDrawer}
      >
        <BlockList.Item.Label>
          <BlockList.Item.Label.Text>
            <FormattedMessage id="settings.integrations.create.automation.matching.label" />
          </BlockList.Item.Label.Text>
        </BlockList.Item.Label>
        <BlockList.Item.Content>
          {salesforceObject && (
            <Content
              contentFor={ConditionTypes.Matches}
              selectedConditions={matches}
              salesforceObject={salesforceObject}
              loading={status === 'pending' || isFetching}
            />
          )}
        </BlockList.Item.Content>
      </BlockList.Item>
      <TriggerSelectionDrawer
        apiStatus={status === 'pending' || isFetching ? 'pending' : 'success'}
        value={matches || defaultFilter}
        fields={fields ?? []}
        onClose={closeTriggerSelectionDrawer}
        visible={triggerSelectionDrawerVisible}
        title={
          <FormattedMessage id="settings.integrations.create.automation.matching.label" />
        }
        breadcrumbs={[]}
        onSubmit={(matches) => {
          setAutomationDetails({ matches });
        }}
      />
    </>
  );
};
